/* eslint-disable react-hooks/exhaustive-deps */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApiRequest } from './useApiRequest';

export type DataLoading = {
    loading: true;
    data: undefined;
    error: undefined;
};

export type DataError = {
    loading: undefined;
    data: undefined;
    error: Error;
};

export type DataComplete<R> = {
    loading: false;
    data: R;
    error: undefined;
};

type QueryState<R> = DataLoading | DataError | DataComplete<R>;

export interface QueryOptions {
    path: string;
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
    params?: Record<string, unknown>;
    pollInterval?: number;
}

function useQueryBase<R>({ path, method = 'GET', params }: QueryOptions) {
    const active = useRef(true);
    const [state, setState] = useState<QueryState<R>>({
        data: undefined,
        loading: true,
        error: undefined,
    });

    const { makeRequest } = useApiRequest();

    const reload = useCallback(
        (completeReload = false) => {
            (async () => {
                if (completeReload) {
                    setState({
                        loading: true,
                        data: undefined,
                        error: undefined,
                    });
                }

                const data = await makeRequest<R>({ path, method, params });

                if (!active.current) {
                    return;
                }

                setState({
                    loading: false,
                    data,
                    error: undefined,
                });
            })().catch(error => {
                setState({
                    loading: undefined,
                    data: undefined,
                    error,
                });
            });
        },
        [makeRequest, path, method, JSON.stringify(params)],
    );

    useEffect(() => {
        active.current = true;
        return () => {
            active.current = false;
        };
    }, []);

    return { state, setState, reload };
}

export default function useQuery<R>(options: QueryOptions) {
    const { state, reload } = useQueryBase<R>(options);

    useEffect(() => {
        reload();

        const interval = options.pollInterval
            ? setInterval(reload, options.pollInterval)
            : undefined;

        return () => {
            if (interval && options.pollInterval) {
                clearInterval(interval);
            }
        };
    }, [JSON.stringify(options.params)]);

    return useMemo(
        () => ({
            ...state,
            reload,
        }),
        [state, reload],
    );
}

export function useDeferredQuery<R>(options: QueryOptions & { shouldExecute: boolean }) {
    const { state, setState, reload } = useQueryBase<R>(options);

    useEffect(() => {
        if (!options.shouldExecute) {
            setState({
                loading: false,
                data: undefined as unknown as R,
                error: undefined,
            });
            return;
        }

        reload();

        const interval = options.pollInterval
            ? setInterval(reload, options.pollInterval)
            : undefined;

        return () => {
            if (interval && options.pollInterval) {
                clearInterval(interval);
            }
        };
    }, [JSON.stringify(options.params), options.shouldExecute]);

    return useMemo(
        () => ({
            ...state,
            reload,
        }),
        [state, reload],
    );
}
